import { format, parseISO } from 'date-fns';

import { GenericResponse } from 'services/api/base';
import { RelatorioAprovacaoConfig, RelatorioAprovacaoSecao } from 'helper/Relatorios/Aprovacao/config';
import {
  AprovacaoEvento,
  AprovacaoSecao,
  AprovacaoStatus,
  Calendario,
  EdicaoStatus,
} from 'types/Relatorios/Aprovacao';

/** Rótulo de uma seção (cai no próprio slug se desconhecida). */
export const labelDaSecao = (slug: string, secoes: RelatorioAprovacaoSecao[]): string => (
  secoes.find((secao) => secao.slug === slug)?.label ?? slug
);

/** Rótulo do valor da variante (cenário/modo) do informe. */
export const labelDaVariante = (
  config: RelatorioAprovacaoConfig,
  valor: string | undefined,
): string => (valor === undefined ? '' : (config.variante.labels[valor] ?? valor));

/** Valor da variante (`cenario` ou `modo`) presente na resposta de aprovação. */
export const varianteDoStatus = (
  config: RelatorioAprovacaoConfig,
  status: AprovacaoStatus,
): string | undefined => status[config.variante.campo];

/** Quantas seções estão com status `aprovada`. */
export const contarAprovadas = (secoes: AprovacaoSecao[]): number => (
  secoes.filter((secao) => secao.status === 'aprovada').length
);

/** Só é possível publicar com todas as seções aprovadas e o dia ainda não publicado. */
export const podePublicar = (statusDia: AprovacaoStatus): boolean => (
  statusDia.status !== 'publicado' && contarAprovadas(statusDia.secoes) === statusDia.secoes.length
);

/** Percentual inteiro arredondado (evita divisão por zero). */
export const percentual = (aprovadas: number, total: number): number => (
  total === 0 ? 0 : Math.round((aprovadas / total) * 100)
);

/** `'2026-05-22'` → `'22/05/2026'`. Sempre via `parseISO` (nunca `new Date(string)`). */
export const formatarDataBR = (dataRodadaIso: string): string => (
  format(parseISO(dataRodadaIso), 'dd/MM/yyyy')
);

/**
 * Data do informe nas frases da aprovação: `config.rotuloDaData` quando o informe define (DESSEM:
 * "13/10/2026 (previsão para 14/10)"), senão `dd/MM/yyyy`.
 */
export const rotuloDaDataDoInforme = (
  config: RelatorioAprovacaoConfig,
  dataRodadaIso: string,
): string => (config.rotuloDaData ?? formatarDataBR)(dataRodadaIso);

/** Instante ISO (`...Z`) → `"22/05/2026 às 12:14"` (horário local do navegador). */
export const formatarDataHora = (instanteIso: string): string => (
  format(parseISO(instanteIso), "dd/MM/yyyy 'às' HH:mm")
);

/** Instante ISO (`...Z`) → `"22/05/2026 12:14"` (horário local), para a faixa compacta. */
export const formatarDataHoraCurta = (instanteIso: string): string => (
  format(parseISO(instanteIso), 'dd/MM/yyyy HH:mm')
);

/** Instante ISO (`...Z`) → `"14/10/25 02:25"` (horário local), para a lista mobile de aprovação. */
export const formatarDataHoraCompacta = (instanteIso: string): string => (
  format(parseISO(instanteIso), 'dd/MM/yy HH:mm')
);

export interface AutoriaDaSecao { acao: 'aprovada' | 'editada'; por: string; em: string; }

/** Quem/quando mostrar numa linha de seção: a aprovação, senão a última edição. */
export const autoriaDaSecao = (secao: AprovacaoSecao): AutoriaDaSecao | null => {
  const autoria = secao.aprovacao ?? secao.ultima_edicao;
  if (!autoria) return null;
  return {
    acao: secao.status === 'aprovada' ? 'aprovada' : 'editada',
    por: autoria.por,
    em: autoria.em,
  };
};

/**
 * Linha "Estudo <ds_name> processado em dd/MM/yyyy às HH:mm" da `RevisaoBar`, com
 * " · contingência <ds_name> processado em …" quando o release tem simulação de contingência
 * processada. Fonte: `simulacoes` (admin, com o nome do estudo) e, sem ela, só
 * `estudo_processado_em` ("Estudo processado em …", sem nome). `null` quando não há instante
 * a mostrar (outros informes, mocks antigos, release sem o dado).
 */
export const descreverProcessamento = (status: AprovacaoStatus): string | null => {
  const principal = status.simulacoes?.principal;
  const principalEm = principal?.processado_em ?? status.estudo_processado_em ?? null;
  if (principalEm === null) return null;

  const nome = principal ? ` ${principal.ds_name}` : '';
  const partes = [`Estudo${nome} processado em ${formatarDataHora(principalEm)}`];

  const contingencia = status.simulacoes?.contingencia;
  if (contingencia?.processado_em) {
    partes.push(
      `contingência ${contingencia.ds_name} processado em ${formatarDataHora(contingencia.processado_em)}`,
    );
  }
  return partes.join(' · ');
};

/**
 * Aviso da `RevisaoBar` quando o Core processou um estudo mais novo que o do release, mas o
 * backend não relinkou porque já há aprovação (`simulacoes.desatualizado`). `null` para cliente
 * (sem `simulacoes`) e quando o release está em dia.
 */
export const descreverDesatualizado = (status: AprovacaoStatus): string | null => {
  const sim = status.simulacoes;
  if (!sim?.desatualizado) return null;
  const novo = sim.core_atual?.principal;
  const qual = novo?.processado_em
    ? ` (${novo.ds_name}, processado em ${formatarDataHora(novo.processado_em)})`
    : '';
  return `Há um estudo mais novo no Core${qual}. Para usá-lo, revogue as aprovações deste informe.`;
};

/**
 * Iniciais para o `Avatar`. `null` (autor "Sistema", eventos `gerado`) usa `'SY'`
 * para não colidir com iniciais reais de uma única letra.
 */
export const iniciais = (nome: string | null): string => {
  if (nome === null) return 'SY';
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return 'SY';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return `${partes[0][0]}${partes[partes.length - 1][0]}`.toUpperCase();
};

const pluralOuSingular = (
  secoes: string[],
  todas: RelatorioAprovacaoSecao[],
  singular: (label: string) => string,
  verboPlural: string,
): string => {
  const totalSecoes = todas.length;
  if (secoes.length === totalSecoes) return `${verboPlural} todas as ${totalSecoes} seções`;
  if (secoes.length === 1) return singular(labelDaSecao(secoes[0], todas));
  return `${verboPlural} ${secoes.length} seções`;
};

export interface EventoDescrito { autor: string; texto: string; }

/**
 * Texto legível do histórico de atividade (card "Histórico de Atividade").
 * `valorVariante` é o cenário/modo da edição (rotulado via `config.variante`).
 */
export const descreverEvento = (
  evento: AprovacaoEvento,
  config: RelatorioAprovacaoConfig,
  valorVariante: string | undefined,
): EventoDescrito => {
  const autor = evento.autor ?? 'Sistema';
  const { secoes } = config;

  switch (evento.tipo) {
    case 'gerado':
      return {
        autor,
        texto: `gerou o informe automaticamente (${config.variante.rotulo} ${labelDaVariante(config, valorVariante)})`,
      };
    case 'aprovado':
      return {
        autor,
        texto: pluralOuSingular(evento.secoes, secoes, (label) => `aprovou a seção ${label}`, 'aprovou'),
      };
    case 'revogado':
      return {
        autor,
        texto: pluralOuSingular(
          evento.secoes,
          secoes,
          (label) => `revogou a aprovação da seção ${label}`,
          'revogou',
        ),
      };
    case 'editado':
      return {
        autor,
        texto: pluralOuSingular(evento.secoes, secoes, (label) => `editou a seção ${label}`, 'editou'),
      };
    case 'publicado':
      return { autor, texto: 'publicou o informe' };
    case 'reaberto':
    default:
      return { autor, texto: 'reabriu o informe para edição' };
  }
};

/**
 * Cor do avatar do histórico por tipo de evento (Figma): aprovação/publicação
 * em verde, ações humanas de edição (editar, revogar, reabrir) em azul e
 * eventos do sistema em cinza.
 */
export const corDoEvento = (tipo: AprovacaoEvento['tipo']): string => {
  switch (tipo) {
    case 'aprovado':
    case 'publicado':
      return '#1E7A4D';
    case 'editado':
    case 'revogado':
    case 'reaberto':
      return '#598CBB';
    case 'gerado':
    default:
      return '#8B96A1';
  }
};

/** `Calendario` → `Record<data ISO, status>`, para lookup O(1) no `DatePicker`. */
export const mapearCalendario = (
  calendario: Calendario,
): Record<string, EdicaoStatus> => (
  calendario.dias.reduce<Record<string, EdicaoStatus>>((acc, dia) => {
    acc[dia.data] = dia.status;
    return acc;
  }, {})
);

/**
 * Extrai o slug ativo a partir do pathname da rota (`/relatorios/<informe>/<slug>`).
 * `aprovacao` (painel) nunca é slug de seção.
 */
export const slugDaRota = (
  pathname: string,
  secoes: RelatorioAprovacaoSecao[],
): string | null => {
  const segmentos = pathname.split('/').filter(Boolean);
  const ultimo = segmentos[segmentos.length - 1];
  if (ultimo === undefined || ultimo === 'aprovacao') return null;
  const encontrado = secoes.find((secao) => secao.slug === ultimo);
  return encontrado ? encontrado.slug : null;
};

export const transformAprovacaoResponse = (
  response: GenericResponse<AprovacaoStatus>,
): AprovacaoStatus => response.data;

export const transformCalendarioResponse = (
  response: GenericResponse<Calendario>,
): Calendario => response.data;
